/**
 * URL validation utilities for website URLs
 */

const DEV_S3_DOMAIN = "startupfuel-development.s3.us-east-1.amazonaws.com";

/**
 * Strip query string and fragment from a URL so signed GCS/S3 links
 * can be compared on their underlying object path.
 */
function stripUrlQuery(url: string): string {
  return url.split("?")[0].split("#")[0];
}

/**
 * Resolve a persisted company-file `_id` for a given `file_url` by
 * matching against the deal's `company_files`. Source payloads in the
 * report components don't carry the file id, so we look it up from
 * the current deal. Matching strips query strings to tolerate signed
 * URLs that re-sign per request.
 *
 * Returns undefined when no match is found; callers should fall back
 * to direct-fetch behavior in that case.
 */
export function resolveCompanyFileId(
  fileUrl: string | undefined | null,
  companyFiles:
    | ReadonlyArray<{ _id: string; file_url: string }>
    | undefined
    | null
): string | undefined {
  if (!fileUrl || !companyFiles?.length) return undefined;
  const target = stripUrlQuery(fileUrl);
  return companyFiles.find((f) => stripUrlQuery(f.file_url) === target)?._id;
}

/**
 * Returns true if the file URL is from S3 (dev bucket or any S3).
 * Used to hide deal radar / S3-origin files in data room and elsewhere.
 * Matches backend startupfuel/utils/url.ts isS3FileUrl.
 */
export function isDevS3File(url: string | undefined | null): boolean {
  if (!url) return false;
  return (
    url.includes(DEV_S3_DOMAIN) ||
    url.includes(".s3.") ||
    url.includes("s3.amazonaws.com")
  );
}

/**
 * Validates if a string is a valid URL
 * @param url - The URL string to validate
 * @returns boolean indicating if the URL is valid
 */
export function isValidUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false;

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return false;

  try {
    // Try to construct a URL object - this validates the URL format
    const urlObj = new URL(
      trimmedUrl.startsWith("http") ? trimmedUrl : `https://${trimmedUrl}`
    );

    // Check for valid protocol
    if (!["http:", "https:"].includes(urlObj.protocol)) {
      return false;
    }

    // Check for a valid hostname (must have at least one dot for a domain)
    const hostname = urlObj.hostname;
    
    // Check for invalid characters in hostname
    // Valid hostnames can only contain a-z, 0-9, dots, and hyphens (RFC 952/1123)
    if (!/^[a-zA-Z0-9.-]+$/.test(hostname)) {
      return false;
    }

    if (!hostname || !hostname.includes(".")) {
      return false;
    }

    // Check for edge cases: starts/ends with dot, or consecutive dots
    if (hostname.startsWith('.') || hostname.endsWith('.') || hostname.includes('..')) {
      return false;
    }

    // Check hostname is not just numbers and dots (basic IP check is okay, but prevent gibberish)
    const hostParts = hostname.split(".");
    const lastPart = hostParts[hostParts.length - 1];

    // TLD should be at least 2 characters and contain letters
    // Also ensure it doesn't start or end with a hyphen or dot (though split handles dot)
    if (lastPart.length < 2 || !/^[a-zA-Z]+$/.test(lastPart)) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Normalizes a URL by adding https:// if no protocol is present
 * @param url - The URL string to normalize
 * @returns The normalized URL string
 */
export function normalizeUrl(url: string): string {
  if (!url || typeof url !== "string") return "";

  const trimmedUrl = url.trim();
  if (!trimmedUrl) return "";

  // Add https:// if no protocol is present
  if (!trimmedUrl.startsWith("http://") && !trimmedUrl.startsWith("https://")) {
    return `https://${trimmedUrl}`;
  }

  return trimmedUrl;
}

/**
 * Validates a website URL and returns an error message if invalid
 * @param url - The URL string to validate
 * @returns Error message string if invalid, null if valid
 */
export function getUrlValidationError(url: string): string | null {
  if (!url || !url.trim()) {
    return null; // Empty URL is allowed (optional field)
  }

  if (!isValidUrl(url)) {
    return "Please enter a valid website URL (e.g., https://example.com)";
  }

  return null;
}

/**
 * Validates a LinkedIn URL and returns an error message if invalid
 * @param url - The URL string to validate
 * @returns Error message string if invalid, null if valid
 */
export function getLinkedInUrlValidationError(url: string): string | null {
  if (!url || !url.trim()) {
    return null; // Empty URL is allowed (optional field)
  }

  if (!isValidUrl(url)) {
    return "Please enter a valid LinkedIn URL (e.g., linkedin.com/company/name)";
  }

  const lowerUrl = url.toLowerCase();

  if (!lowerUrl.includes("linkedin.com")) {
    return "Please enter a valid LinkedIn URL (must contain linkedin.com)";
  }

  // Optional: Check for company or profile path
  // Startups usually have /company/ pages, but sometimes people link personal profiles /in/
  // or checks for other valid linkedin paths
  if (!lowerUrl.includes("/company/") && !lowerUrl.includes("/in/")) {
     return "Please enter a valid LinkedIn Company or Profile URL";
  }

  return null;
}
